import { Layout, message, notification } from "antd";
import { useEffect } from "react";
import { Route } from "react-router-dom";
import { userInfoStore } from "Stores/UserInfoStore";

import { PrivateRoute } from "Config/Router";
import { ErrorPage404 } from "Pages/ErrorPage404";
import { SupportPage } from "Pages/Support/Support";
// import { Loader } from 'Components/General/Loader';
import { Header } from "Components/Core/Header";
import { Admin } from "Pages/Admin/Admin";
import { DirectURLs } from "Pages/Login/Pages/ExternalLogin/DirectURLs";
import { loginSettingsData } from "Pages/Login/Stores/LoginSettingsStore";
import { ProjectRouter } from "Pages/Project/ProjectRouter";
import { UserSettings } from "Pages/UserSettings/UserSettings";
import { Routes } from "react-router-dom";
import { LoadAsync } from "Components/General/Loadable";
import { ModalRoot } from "Components/General/Modal/ModalRoot";

import { ActionProgress } from "Components/ActionProgress/ActionProgress";
import { FileDetailsModal } from "Components/FileDetails/FileDetailsModal";
import { OnlineViewer } from "Components/OnlineViewer";
// import { PdfjsViewerModal } from "Components/PdfJsViewer/PdfjsViewerModal";
import { WOPIViewer } from "Components/WOPIViewer";
import { XKTViewerModal } from "Components/XKTViewer/XKTViewerModal";
import { fabric as Fabric } from "fabric";
import { Logout } from "Pages/Login/Logout";
import { OXAuthentication } from "Pages/Login/Pages/ExternalLogin/OXAuthentication";
import { TaskModal } from "Pages/Project/Tasks/Components/TaskModal";
import { RedirectAccordingToAccess } from "Pages/RedirectAccordingToAccess";
import { TeamsWidget } from "TeamsWidget/TeamsWidget";
import { RouteWithTitle } from "Components/Utils/RouteWithTitle";
import classNames from "classnames";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import "themes/theme-variables.css";
import { NotificationInstance } from "antd/lib/notification/interface";
import { BIMCollabAuth } from "Pages/BIMCollabAuth";
import { AllProvidersProvider } from "AllProvidersProvider";
import { I18nextProvider } from "react-i18next";
import i18nConfig from "Config/i18n";
import { MessageInstance } from "antd/lib/message/interface";
import { ApryseViewerModal } from "Components/ApryseViewer/ApryseViewerModal";
import { AnnotationSettings } from "Components/PdfJsViewer/Components/PDFAnnotation";
import { Suspense } from "react";
import { observer } from "mobx-react";

declare global {
  interface Window {
    umami: umami_ns.umami;
    GLOBAL: {
      API_BASE: string;
      ROUTER_BASE: string;
      PRGROUP: string;
    };
    Loader: { stop() };
    fabric: typeof Fabric;
  }
}

/**
 * App
 *
 * Here are all the Main Routes of the App defined.
 *
 * SubRoutes are defined in their own Module.
 */

const LoginRouter = LoadAsync(() => import(/* webpackChunkName: "login" */ "Pages/Login/LoginRouter"));
const Dashboard = LoadAsync(() => import(/* webpackChunkName: "dashboard" */ "Pages/Dashboard/Pages/Dashboard"));
// tslint:disable-next-line: max-line-length
const ProvidersPrintJob = LoadAsync(
  () => import(/* webpackChunkName: "printjob" */ "Pages/ProvidersPrintJob/ProvidersPrintJob")
);
const Guest = LoadAsync(() => import(/* webpackChunkName: "guest" */ "Pages/Guest/Guest"));
const VerifyEmail = LoadAsync(() => import(/* webpackChunkName: "verifyEmail" */ "Pages/VerifyEmail/VerifyEmail"));
// tslint:disable-next-line: max-line-length
const ViewFullscreen = LoadAsync(
  () => import(/* webpackChunkName: "verifyEmail" */ "Pages/ViewFullscreen/ViewFullscreen")
);

const PdfjsViewerModal = LoadAsync(
  () => import(/* webpackChunkName: "pdfjsviewer" */ "Components/PdfJsViewer/PdfjsViewerModal")
);

// Export from this spot is required to receive the theme settings such as fronts and colors etc.
// these settings are received from Antd ConfigProvider.
export let notificationInstance: NotificationInstance;
export let messageInstance: MessageInstance;

const App = observer(() => {
  const [api, contextHolder] = notification.useNotification({ getContainer: () => document.getElementById("root") });
  const [messageApi, messageContextHolder] = message.useMessage({
    getContainer: () => document.getElementById("root"),
  });
  notificationInstance = api;
  messageInstance = messageApi;

  useEffect(() => {
    (async () => {
      await loginSettingsData.fetch();
      AnnotationSettings.setupFromLocalStorage();
    })();
    if (!userInfoStore.isAuthenticated) {
      userInfoStore.signOut("App: componentDidMount", true);
    }
  }, []);

  return (
    <I18nextProvider i18n={i18nConfig}>
      <AllProvidersProvider>
        <ModalRoot />
        <Layout
          style={{ minHeight: `100%`, width: `100%` }}
          className={classNames({ splittedView: pdfViewerStore.isVisibleSplitView })}
        >
          <Layout.Header style={{ position: "fixed", top: 0, width: "100%", zIndex: 999 }}>
            <Header />
          </Layout.Header>
          <Layout.Content className="content-container" id="layout">
            <Routes>
              <Route path="*" element={<ErrorPage404 />} />
              <Route
                path="/login/*"
                element={
                  <RouteWithTitle pageName={"Login"}>
                    <LoginRouter />
                  </RouteWithTitle>
                }
              />
              <Route
                path="/support"
                element={
                  <RouteWithTitle pageName={"Help"}>
                    <SupportPage />
                  </RouteWithTitle>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute pageName="Dashboard">
                    <RouteWithTitle pageName={"Dashboard"}>
                      <Dashboard />
                    </RouteWithTitle>
                  </PrivateRoute>
                }
              />
              <Route
                path="/guest/*"
                element={
                  <PrivateRoute roles={["GUEST"]} pageName="Guest">
                    <Guest />
                  </PrivateRoute>
                }
              />
              <Route
                path="/view/:projectId/*"
                element={
                  <PrivateRoute>
                    <ViewFullscreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/project/:projectId/*"
                element={
                  <PrivateRoute>
                    <ProjectRouter />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/*"
                element={
                  <PrivateRoute roles={["ADMIN", "PRGROUPADMIN"]}>
                    <RouteWithTitle pageName={"Admin"}>
                      <Admin />
                    </RouteWithTitle>
                  </PrivateRoute>
                }
              />
              <Route
                path="/printjob/:id"
                element={
                  <PrivateRoute>
                    <RouteWithTitle pageName={"Print jobs"}>
                      <ProvidersPrintJob />
                    </RouteWithTitle>
                  </PrivateRoute>
                }
              />
              <Route
                path="/userSettings/*"
                element={
                  <PrivateRoute>
                    <RouteWithTitle pageName={"User settings"}>
                      <UserSettings />
                    </RouteWithTitle>
                  </PrivateRoute>
                }
              />
              <Route
                path="/direct/*"
                element={
                  <PrivateRoute>
                    <DirectURLs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ox/*"
                element={
                  <PrivateRoute>
                    <OXAuthentication />
                  </PrivateRoute>
                }
              />
              <Route
                path="/verifyMail/:token/*"
                element={
                  <PrivateRoute>
                    <VerifyEmail />
                  </PrivateRoute>
                }
              />
              <Route path="/logout" element={<Logout />} />
              <Route path="/" element={<RedirectAccordingToAccess />} />
              <Route path="/404" element={<ErrorPage404 />} />
              <Route path="/callback/bimcollab" element={<BIMCollabAuth />} />
            </Routes>
            <TeamsWidget />
            <XKTViewerModal />
            <OnlineViewer />
            <Suspense fallback={null}>
              <PdfjsViewerModal />
            </Suspense>
            <ApryseViewerModal />
            <WOPIViewer />
            <FileDetailsModal />
            <TaskModal />
            <ActionProgress />
            {contextHolder}
            {messageContextHolder}
          </Layout.Content>
        </Layout>
      </AllProvidersProvider>
    </I18nextProvider>
  );
});

export default App;
